import { LazyFaroMenu } from "@/components/ui/lazy-faro-menu";
import { Theme } from "@emotion/react";
import {
  ArrowDownIcon,
  codeToLanguageCodes,
  FaroButton,
  FaroChip,
  FaroIconButton,
  FaroMenuItem,
  FaroMenuItemProps,
  FaroText,
  FaroTooltip,
  grey,
  LanguageCodes,
  NoTranslate,
  ThreeDotsIcon,
  TruncatedFaroText,
} from "@faro-lotv/flat-ui";
import { useLocalizeLanguage } from "@faro-lotv/foreign-observers";
import { generateRelativeDate } from "@faro-lotv/foundation";
import { ISOTimeString } from "@faro-lotv/ielement-types";
import { Box, Stack, SxProps } from "@mui/system";
import { DateTime } from "luxon";
import {
  Children,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  calculateProgressMenuZIndex,
  ProgressMenuLayer,
} from "../../progress-overview-z-index";

export type GenericCardLayoutProps = {
  /** Name of the uploaded file */
  name: ReactNode;

  /** Optional text describing the task */
  subText?: ReactNode;

  /** Action to offer to the user inside the card */
  action?: CardAction;

  /** Start time of the task */
  startTime: ISOTimeString;

  /** A menu with multiple actions */
  menu?: CardAction[];

  /** When defined, the menu is triggered with a customizable button, instead of a three-dot-icon */
  menuButtonLabel?: ReactNode;

  /** Whether the menu is loading, and should show a skeleton. */
  menuIsLoading?: boolean;

  /** Callback to trigger when the menu is opened. */
  onMenuOpened?(): void;

  /** Optional style to apply to the root element */
  sx?: SxProps<Theme>;

  /** Optional style to apply the the wrapper of the title */
  titleWrapperSx?: SxProps<Theme>;

  /** Optional flag to report this task has been aborted */
  aborted?: boolean;
};

/** @returns the main layout of a menu card */
export function GenericCardLayout({
  name,
  subText,
  action,
  startTime,
  menu,
  menuButtonLabel,
  menuIsLoading,
  onMenuOpened,
  sx,
  titleWrapperSx,
  children,
  aborted,
}: PropsWithChildren<GenericCardLayoutProps>): JSX.Element {
  const date = useMemo(() => DateTime.fromISO(startTime), [startTime]);
  const language = useLocalizeLanguage();
  const appLanguage = codeToLanguageCodes(language.code);
  // The locale used to translate "x days ago" in generateRelativeDate
  const locale = useMemo((): string => {
    // The following switch may need to be updated if LanguageCodes enum is extended
    switch (appLanguage) {
      case LanguageCodes.English:
        return "en-US";
      case LanguageCodes.French:
        return "fr-FR";
      case LanguageCodes.German:
        return "de-DE";
      case LanguageCodes.Spanish:
        return "es-ES";
      case LanguageCodes.Japanese:
        return "ja-JP";
      case LanguageCodes.Korean:
        return "ko-KR";
      case LanguageCodes.Portuguese:
        return "pt-PT";
      case LanguageCodes.Chinese:
        return "zh-Hans";
      case LanguageCodes.Italian:
        return "it-IT";
    }
  }, [appLanguage]);

  const dateString = date.toLocaleString({
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZoneName: "short",
  });

  const generateTimeText = useCallback(
    () =>
      // Passing null as `stringForNow` so generateRelativeDate returns null instead of "now"
      generateRelativeDate(date, locale, null),
    [date, locale],
  );

  const [relativeTime, setRelativeTime] = useState(generateTimeText());
  // Updating the relative time every second to make sure it stays accurate
  useEffect(() => {
    const intervalId = setInterval(() => {
      setRelativeTime(generateTimeText());
    }, 1000);
    return () => clearInterval(intervalId);
  }, [generateTimeText]);

  return (
    <Stack
      id="card-wrapper"
      sx={{
        py: 1.5,
        opacity: aborted ? 0.5 : undefined,
        width: "100%",
        ...sx,
      }}
    >
      <Stack sx={titleWrapperSx}>
        {/* Title row with name, timestamp, and actions */}
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Stack
            direction="row"
            sx={{ alignItems: "center", gap: 0.75, minWidth: 0, flex: 1 }}
          >
            <TruncatedFaroText variant="bodyM" color={grey[900]}>
              {name}
            </TruncatedFaroText>
            <FaroTooltip title={dateString}>
              <FaroText variant="bodyS" color={grey[600]} noWrap>
                {relativeTime ? (
                  <NoTranslate>{relativeTime}</NoTranslate>
                ) : (
                  "now"
                )}
              </FaroText>
            </FaroTooltip>
          </Stack>

          <Box
            component="div"
            sx={{
              display: "flex",
              gap: 0.25,
              flexShrink: 0,
              alignItems: "center",
            }}
          >
            {action && <CardActionButton {...action} />}
            {!!menu?.length && (
              <CardActionMenu
                menu={menu}
                menuButtonLabel={menuButtonLabel}
                menuIsLoading={menuIsLoading}
                onMenuOpened={onMenuOpened}
              />
            )}
            {aborted && (
              <FaroChip
                label="Cancelled"
                backgroundColor={grey[0]}
                borderColor={grey[300]}
                variant="outlined"
              />
            )}
          </Box>
        </Stack>

        {/* Subtitle */}
        {subText && (
          <FaroText variant="bodyXS" color={grey[600]}>
            {subText}
          </FaroText>
        )}
      </Stack>

      {/* Content (progress bar, error messages, etc.) */}
      {Children.toArray(children).some(Boolean) && (
        <Stack component="div" sx={{ gap: 0.75, mt: 1.5 }}>
          {children}
        </Stack>
      )}
    </Stack>
  );
}

export type CardAction = {
  /** Name of the action button */
  name: ReactNode;

  /** Color for the action's menu item */
  color?: FaroMenuItemProps["color"];

  /** Callback executed when the action is triggered */
  callback(): void;

  /** Allow to disable to the action button by providing a message to show */
  disableMessage?: string;

  /** Whether the action is currently loading. Disables the button and shows a loading spinner. */
  isLoading?: boolean;
};

/** @returns the card button to trigger an action */
function CardActionButton({
  name,
  callback,
  disableMessage,
  isLoading,
}: CardAction): JSX.Element {
  return (
    <FaroTooltip title={disableMessage}>
      <FaroButton
        disabled={!!disableMessage}
        variant="ghost"
        onClick={callback}
        loading={isLoading}
        size="s"
      >
        {name}
      </FaroButton>
    </FaroTooltip>
  );
}

/** @returns a three dot menu for a card */
function CardActionMenu({
  menu,
  menuButtonLabel,
  menuIsLoading,
  onMenuOpened,
}: Required<Pick<GenericCardLayoutProps, "menu">> &
  Pick<
    GenericCardLayoutProps,
    "menuButtonLabel" | "menuIsLoading" | "onMenuOpened"
  >): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);

  const openMenu = useCallback(() => {
    setIsOpen(true);
    onMenuOpened?.();
  }, [onMenuOpened]);

  return (
    <>
      {menuButtonLabel ? (
        <FaroButton ref={button} onClick={openMenu} variant="ghost" size="s">
          {menuButtonLabel}{" "}
          <ArrowDownIcon sx={{ fontSize: "0.625rem", ml: 0.25 }} />
        </FaroButton>
      ) : (
        <FaroIconButton ref={button} onClick={openMenu} size="xs">
          <ThreeDotsIcon />
        </FaroIconButton>
      )}

      <LazyFaroMenu
        anchorEl={button.current}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          zIndex: calculateProgressMenuZIndex(ProgressMenuLayer.menu),
        }}
        // While loading, we approximate the width for the skeleton, and shrink to automatically fit later.
        minWidth={menuIsLoading ? 100 : 0}
        isLoading={menuIsLoading}
      >
        {menu.map(({ name, callback, color }, index) => (
          <FaroMenuItem
            key={index}
            label={name}
            color={color}
            highlightColor={color}
            onClick={callback}
          />
        ))}
      </LazyFaroMenu>
    </>
  );
}
